/**
 * Code Splitting Loader for Kent Traders
 * Dynamically loads JavaScript modules based on page context and user interaction
 * Reduces initial bundle size and improves page load performance
 */

(function() {
  'use strict';

  class CodeSplitLoader {
    constructor() {
      this.loadedModules = new Set();
      this.loadingPromises = new Map();
      this.config = {
        // Critical modules - load immediately
        critical: [
          'accessibility-enhancements.js',
          'error-handler.js',
          'browser-env-setup-firebase.js'
        ],
        // Page-specific modules - load based on template
        pageSpecific: {
          'product': ['product-modules-compact.js', 'variant-display-fix.js', 'kt-product-enhancements.js'],
          'collection': ['facets.js'],
          'cart': ['cart-drawer-fix.js', 'bulk-pricing-discount-manager.js']
        },
        // Interaction-based modules - load on user interaction
        // (kt-advanced-search.js removed with the KT-111 search consolidation)
        onInteraction: [
          'kt-search-analytics.js',
          'enhanced-keyboard-navigation.js'
        ],
        // Below-fold modules - load after page load
        belowFold: [
          'kt-back-in-stock.js',
          'kt-alert-manager-modern.js',
          'seo-enhancer.js'
        ]
      };
      this.init();
    }

    init() {
      // Load critical modules immediately
      this.loadCritical();
      
      // Load page-specific modules
      this.loadPageSpecific();
      
      // Load interaction-based modules
      this.setupInteractionLoading();
      
      // Load below-fold modules after page load
      this.setupBelowFoldLoading();
    }

    /**
     * Load critical modules immediately
     */
    loadCritical() {
      this.config.critical.forEach(module => {
        this.loadModule(module, { priority: 'high' });
      });
    }

    /**
     * Load page-specific modules based on current template
     */
    loadPageSpecific() {
      const template = this.getTemplate();
      const modules = this.config.pageSpecific[template] || [];
      
      modules.forEach(module => {
        this.loadModule(module, { priority: 'medium' });
      });
    }

    /**
     * Setup interaction-based module loading
     */
    setupInteractionLoading() {
      let interacted = false;
      
      const loadOnInteraction = () => {
        if (interacted) return;
        interacted = true;
        
        this.config.onInteraction.forEach(module => {
          this.loadModule(module, { priority: 'low' });
        });
      };

      // Load on first user interaction
      ['mousedown', 'touchstart', 'keydown'].forEach(event => {
        document.addEventListener(event, loadOnInteraction, { once: true, passive: true });
      });

      // Fallback: load after 2 seconds
      setTimeout(loadOnInteraction, 2000);
    }

    /**
     * Setup below-fold module loading
     */
    setupBelowFoldLoading() {
      if (document.readyState === 'complete') {
        this.loadBelowFold();
      } else {
        window.addEventListener('load', () => this.loadBelowFold(), { once: true });
      }
    }

    /**
     * Load below-fold modules
     */
    loadBelowFold() {
      // Use requestIdleCallback if available, otherwise setTimeout
      const scheduleLoad = window.requestIdleCallback || ((fn) => setTimeout(fn, 1));
      
      scheduleLoad(() => {
        this.config.belowFold.forEach(module => {
          this.loadModule(module, { priority: 'low' });
        });
      });
    }

    /**
     * Load a module dynamically
     */
    loadModule(src, options = {}) {
      // Check if already loaded or loading
      if (this.loadedModules.has(src)) {
        return Promise.resolve();
      }

      if (this.loadingPromises.has(src)) {
        return this.loadingPromises.get(src);
      }

      const promise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = this.getAssetUrl(src);
        script.async = true;
        script.defer = options.priority !== 'high';
        
        script.onload = () => {
          this.loadedModules.add(src);
          this.loadingPromises.delete(src);
          resolve();
        };
        
        script.onerror = () => {
          this.loadingPromises.delete(src);
          reject(new Error(`Failed to load module: ${src}`));
        };

        // Add to head with priority handling
        if (options.priority === 'high') {
          document.head.insertBefore(script, document.head.firstChild);
        } else {
          document.head.appendChild(script);
        }
      });

      this.loadingPromises.set(src, promise);
      return promise;
    }

    /**
     * Get asset URL (Shopify asset_url filter equivalent)
     */
    getAssetUrl(filename) {
      // In Shopify, this would use the asset_url filter
      // For now, assume assets are in /assets/ directory
      if (filename.startsWith('http://') || filename.startsWith('https://')) {
        return filename;
      }
      return `/assets/${filename}`;
    }

    /**
     * Get current template/page type
     */
    getTemplate() {
      const body = document.body;
      if (!body) return 'index';
      
      // Check for template classes
      const templateClass = Array.from(body.classList).find(cls => 
        cls.startsWith('template-')
      );
      
      if (templateClass) {
        return templateClass.replace('template-', '');
      }
      
      // Fallback: check pathname
      const path = window.location.pathname;
      if (path.includes('/products/')) return 'product';
      if (path.includes('/collections/')) return 'collection';
      if (path.includes('/cart')) return 'cart';
      if (path.includes('/account')) return 'customers';
      
      return 'index';
    }

    /**
     * Preload a module (for critical paths)
     */
    preloadModule(src) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = this.getAssetUrl(src);
      document.head.appendChild(link);
    }

    /**
     * Load multiple modules in parallel
     */
    loadModules(sources, options = {}) {
      return Promise.allSettled(
        sources.map(src => this.loadModule(src, options))
      );
    }
  }

  // Initialize loader
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.codeSplitLoader = new CodeSplitLoader();
    });
  } else {
    window.codeSplitLoader = new CodeSplitLoader();
  }

  // Export for use in other scripts
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = CodeSplitLoader;
  }
})();
